import {
  PRIVACY_DEFAULTS,
  SELLER_RETENTION_DAYS,
  TENANT_STATUSES,
  TENANT_STATUS_LABELS,
  isValidTaxNumber,
  sellerRetentionProblem,
  type PostalAddress,
  type TenantDetail,
  type TenantStatus,
  type TenantSummary,
} from '@wms/domain';
import { useState } from 'react';
import { api, errorMessage } from '../../api';
import { fmt } from '../../format';
import { temporaryPassword } from '../../passwords';
import { navigate } from '../../router';
import { useSession } from '../../session';
import { Empty, ErrorBanner, SellerChip, Spinner, useLoad, useToast } from '../../ui';
import { Icon } from '../../ui/icons';
import { useSort } from '../../ui/sort';
import { PricingEditor, PricingPanel, TemplatePicker, loadTemplate, pricingPayload, type PricingDraft } from './Pricing';
import { UsersPanel } from './Users';

const STATUS_TONE: Record<TenantStatus, string> = { onboarding: 'info', active: 'ok', suspended: 'warn', offboarded: '' };

/** The company fields as typed (all strings), shared by the new-customer wizard and the detail page. */
interface CompanyForm {
  readonly code: string;
  readonly displayName: string;
  readonly legalName: string;
  readonly taxNumber: string;
  readonly taxOffice: string;
  readonly contactName: string;
  readonly contactEmail: string;
  readonly contactPhone: string;
  readonly line1: string;
  readonly district: string;
  readonly city: string;
  readonly postalCode: string;
}

const EMPTY_COMPANY: CompanyForm = {
  code: '',
  displayName: '',
  legalName: '',
  taxNumber: '',
  taxOffice: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  line1: '',
  district: '',
  city: '',
  postalCode: '',
};

const TR_ASCII: Record<string, string> = { Ç: 'C', Ğ: 'G', İ: 'I', I: 'I', Ö: 'O', Ş: 'S', Ü: 'U' };
/** "Yeni Kozmetik" → "YENIKOZM": a code suggestion, editable. */
export function suggestCode(name: string): string {
  return name
    .toLocaleUpperCase('tr')
    .split('')
    .map((c) => TR_ASCII[c] ?? c)
    .join('')
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 8);
}

function companyFrom(t: TenantDetail): CompanyForm {
  return {
    code: t.code,
    displayName: t.displayName,
    legalName: t.legalName,
    taxNumber: t.taxNumber ?? '',
    taxOffice: t.taxOffice ?? '',
    contactName: t.contactName ?? '',
    contactEmail: t.contactEmail ?? '',
    contactPhone: t.contactPhone ?? '',
    line1: t.billingAddress?.line1 ?? '',
    district: t.billingAddress?.district ?? '',
    city: t.billingAddress?.city ?? '',
    postalCode: t.billingAddress?.postalCode ?? '',
  };
}

function companyProblems(f: CompanyForm, withCode: boolean): string[] {
  const problems: string[] = [];
  if (withCode && !/^[A-Z0-9]{2,10}$/.test(f.code)) problems.push('Kod 2–10 büyük harf veya rakam olmalı');
  if (!f.displayName.trim()) problems.push('Görünen ad gerekli');
  if (!f.legalName.trim()) problems.push('Ticari unvan gerekli');
  if (f.taxNumber && !isValidTaxNumber(f.taxNumber)) problems.push('Vergi numarası 10 haneli VKN veya geçerli 11 haneli TCKN olmalı');
  if (f.postalCode && !/^\d{5}$/.test(f.postalCode)) problems.push('Posta kodu 5 haneli olmalı');
  const address = [f.line1, f.district, f.city].filter((v) => v.trim());
  if (address.length > 0 && address.length < 3) problems.push('Fatura adresinde adres, ilçe ve il birlikte girilmeli');
  return problems;
}

function companyBody(f: CompanyForm) {
  const address: PostalAddress | null =
    f.line1.trim() && f.district.trim() && f.city.trim()
      ? {
          fullName: f.legalName.trim(),
          company: f.legalName.trim(),
          phone: f.contactPhone.trim() || null,
          line1: f.line1.trim(),
          line2: null,
          neighborhood: null,
          district: f.district.trim(),
          city: f.city.trim(),
          postalCode: f.postalCode.trim() || null,
          countryCode: 'TR',
        }
      : null;
  return {
    legalName: f.legalName.trim(),
    displayName: f.displayName.trim(),
    taxNumber: f.taxNumber.trim() || null,
    taxOffice: f.taxOffice.trim() || null,
    contactName: f.contactName.trim() || null,
    contactEmail: f.contactEmail.trim() || null,
    contactPhone: f.contactPhone.trim() || null,
    billingAddress: address,
  };
}

function CompanyFields({ value, onChange, codeLocked }: { value: CompanyForm; onChange: (next: CompanyForm) => void; codeLocked: boolean }) {
  const set = (key: keyof CompanyForm) => (e: { target: { value: string } }) => onChange({ ...value, [key]: e.target.value });
  return (
    <div className="stack">
      <div className="form-grid">
        <div className="field">
          <label htmlFor="c-display">Görünen ad</label>
          <input
            id="c-display"
            className="input"
            value={value.displayName}
            onChange={(e) =>
              onChange({
                ...value,
                displayName: e.target.value,
                // the code follows the name until it is typed by hand
                code: codeLocked || value.code !== suggestCode(value.displayName) ? value.code : suggestCode(e.target.value),
              })
            }
            placeholder="Kuzey Spor"
          />
        </div>
        <div className="field">
          <label htmlFor="c-code">Müşteri kodu</label>
          <input
            id="c-code"
            className="input mono"
            value={value.code}
            disabled={codeLocked}
            maxLength={10}
            onChange={(e) => onChange({ ...value, code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') })}
          />
          <span className="hint">Toplama listesinde ve etiketlerde görünür; sonradan değişmez</span>
        </div>
        <div className="field wide">
          <label htmlFor="c-legal">Ticari unvan</label>
          <input id="c-legal" className="input" value={value.legalName} onChange={set('legalName')} placeholder="Kuzey Spor Ürünleri A.Ş." />
        </div>
        <div className="field">
          <label htmlFor="c-tax">VKN / TCKN</label>
          <input id="c-tax" className="input mono" inputMode="numeric" maxLength={11} value={value.taxNumber} onChange={set('taxNumber')} />
        </div>
        <div className="field">
          <label htmlFor="c-office">Vergi dairesi</label>
          <input id="c-office" className="input" value={value.taxOffice} onChange={set('taxOffice')} />
        </div>
        <div className="field">
          <label htmlFor="c-contact">Yetkili kişi</label>
          <input id="c-contact" className="input" value={value.contactName} onChange={set('contactName')} />
        </div>
        <div className="field">
          <label htmlFor="c-email">İletişim e-postası</label>
          <input id="c-email" className="input" type="email" value={value.contactEmail} onChange={set('contactEmail')} />
        </div>
        <div className="field">
          <label htmlFor="c-phone">Telefon</label>
          <input id="c-phone" className="input" type="tel" value={value.contactPhone} onChange={set('contactPhone')} />
        </div>
      </div>
      <h3>Fatura adresi</h3>
      <div className="form-grid">
        <div className="field wide">
          <label htmlFor="c-line1">Adres</label>
          <input id="c-line1" className="input" value={value.line1} onChange={set('line1')} placeholder="Mahalle, cadde, no" />
        </div>
        <div className="field">
          <label htmlFor="c-district">İlçe</label>
          <input id="c-district" className="input" value={value.district} onChange={set('district')} />
        </div>
        <div className="field">
          <label htmlFor="c-city">İl</label>
          <input id="c-city" className="input" value={value.city} onChange={set('city')} />
        </div>
        <div className="field">
          <label htmlFor="c-postal">Posta kodu</label>
          <input id="c-postal" className="input mono" inputMode="numeric" maxLength={5} value={value.postalCode} onChange={set('postalCode')} />
        </div>
      </div>
    </div>
  );
}

/** Admins' customer list: who is with us, in which state, and how much of the warehouse they use. */
export function Tenants() {
  const session = useSession();
  const list = useLoad(() => api<TenantSummary[]>('GET', '/api/admin/tenants'), []);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<TenantStatus | ''>('');
  const sort = useSort<TenantSummary>('tenants', {
    tenant: { label: 'Müşteri', value: (t) => t.displayName },
    taxNumber: { label: 'VKN / TCKN', value: (t) => t.taxNumber },
    status: { label: 'Durum', value: (t) => TENANT_STATUSES.indexOf(t.status) },
    users: { label: 'Kullanıcı', value: (t) => t.activeUsers, desc: true },
    products: { label: 'Ürün', value: (t) => t.products, desc: true },
    stock: { label: 'Stok (adet)', value: (t) => t.unitsInStock, desc: true },
    openOrders: { label: 'Açık sipariş', value: (t) => t.openOrders, desc: true },
    // own prices first
    pricing: { label: 'Fiyat', value: (t) => t.customPricing, desc: true },
  });
  const term = search.trim().toLocaleLowerCase('tr');
  const rows = sort.apply(
    (list.data ?? []).filter(
      (t) => (!status || t.status === status) && (!term || `${t.code} ${t.displayName} ${t.legalName} ${t.taxNumber ?? ''}`.toLocaleLowerCase('tr').includes(term)),
    ),
  );
  return (
    <>
      <div className="row">
        {sort.picker}
        <input className="input" style={{ maxWidth: 280 }} placeholder="Ad, kod veya VKN ara" value={search} onChange={(e) => setSearch(e.target.value)} />
        <div className="row" style={{ gap: 6 }}>
          {(['', 'active', 'onboarding', 'suspended', 'offboarded'] as const).map((s) => (
            <button key={s} className={`chip select${status === s ? ' on' : ''}`} onClick={() => setStatus(s)}>
              {s ? TENANT_STATUS_LABELS[s] : 'Tümü'}
            </button>
          ))}
        </div>
        <span className="spacer" style={{ flex: 1 }} />
        {session.isAdmin && (
          <a className="btn primary" href="#/yonetim/musteriler/yeni">
            <Icon name="plus" /> Yeni müşteri
          </a>
        )}
      </div>
      <section className="card">
        {list.error && <ErrorBanner message={list.error} onRetry={() => void list.reload()} />}
        {!list.data ? (
          <Spinner />
        ) : rows.length === 0 ? (
          <Empty title="Müşteri yok">{session.isAdmin ? '"Yeni müşteri" ile ilk müşterinizi açın.' : null}</Empty>
        ) : (
          <div className="table-wrap">
            <table className="t stack-sm">
              <thead>
                <tr>
                  {sort.th('tenant')}
                  {sort.th('taxNumber', 'hide-sm')}
                  {sort.th('status')}
                  {sort.th('users', 'num hide-sm')}
                  {sort.th('products', 'num hide-sm')}
                  {sort.th('stock', 'num')}
                  {sort.th('openOrders', 'num')}
                  {sort.th('pricing', 'hide-sm')}
                </tr>
              </thead>
              <tbody>
                {rows.map((t) => (
                  <tr key={t.id} className="clickable" onClick={() => navigate(`/yonetim/musteriler/${t.id}`)}>
                    <td>
                      <div className="row" style={{ gap: 8 }}>
                        <SellerChip code={t.code} />
                        <div>
                          <b>{t.displayName}</b>
                          <div className="muted" style={{ fontSize: 13 }}>
                            {t.legalName}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="mono hide-sm">{t.taxNumber ?? '—'}</td>
                    <td>
                      <span className={`chip ${STATUS_TONE[t.status]}`}>{TENANT_STATUS_LABELS[t.status]}</span>
                    </td>
                    <td className="num hide-sm">{t.activeUsers}</td>
                    <td className="num hide-sm">{fmt.n(t.products)}</td>
                    <td className="num" data-label="Stok">{fmt.n(t.unitsInStock)}</td>
                    <td className="num" data-label="Açık sipariş">{fmt.n(t.openOrders)}</td>
                    <td className="hide-sm">{t.customPricing ? <span className="chip info">Özel</span> : <span className="muted">Liste</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

const STEPS = ['Firma', 'Firma yöneticisi', 'Fiyatlandırma', 'Özet'] as const;

/** Opening a customer: company, its first user (firma yöneticisi), its prices; then a summary. */
export function NewTenant() {
  const toast = useToast();
  const tenants = useLoad(() => api<TenantSummary[]>('GET', '/api/admin/tenants'), []);
  const [step, setStep] = useState(0);
  const [company, setCompany] = useState<CompanyForm>(EMPTY_COMPANY);
  const [active, setActive] = useState(true);
  const [withOwner, setWithOwner] = useState(true);
  const [owner, setOwner] = useState({ fullName: '', email: '', password: temporaryPassword() });
  const [pricingMode, setPricingMode] = useState<'list' | 'custom'>('list');
  const [template, setTemplate] = useState('');
  const [draft, setDraft] = useState<PricingDraft | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<{ tenant: TenantDetail; password: string | null; pricingError: string | null } | null>(null);

  const problems = [
    ...(step >= 0 ? companyProblems(company, true) : []),
    ...(step >= 1 && withOwner && (!owner.fullName.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(owner.email)) ? ['Firma yöneticisinin adı ve e-postası gerekli'] : []),
  ];
  const stepProblems = step === 0 ? companyProblems(company, true) : step === 1 ? problems.slice(companyProblems(company, true).length) : [];

  const usePricing = async (mode: 'list' | 'custom', from = template) => {
    setPricingMode(mode);
    if (mode === 'custom') {
      try {
        setDraft(await loadTemplate(from, company.code || 'özel'));
      } catch (e) {
        toast(errorMessage(e), 'bad');
      }
    }
  };

  const create = async () => {
    setBusy(true);
    setError(null);
    let payload: { body: unknown } | null = null;
    if (pricingMode === 'custom' && draft) {
      const p = pricingPayload(draft);
      if ('problem' in p) {
        setError(p.problem);
        setBusy(false);
        return;
      }
      payload = p;
    }
    try {
      const tenant = await api<TenantDetail>('POST', '/api/admin/tenants', {
        code: company.code,
        ...companyBody(company),
        status: active ? 'active' : 'onboarding',
        ...(withOwner ? { owner: { fullName: owner.fullName.trim(), email: owner.email.trim(), password: owner.password } } : {}),
      });
      let pricingError: string | null = null;
      if (payload) {
        try {
          await api('PUT', `/api/admin/tenants/${tenant.id}/pricing`, payload.body);
        } catch (e) {
          pricingError = errorMessage(e);
        }
      }
      setDone({ tenant, password: withOwner ? owner.password : null, pricingError });
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <section className="card narrow">
        <div className="card-b stack">
          <div className="banner ok">
            <Icon name="check" />
            <div>
              <div className="big">{done.tenant.displayName} açıldı</div>
              Müşteri kodu {done.tenant.code}; durum: {TENANT_STATUS_LABELS[done.tenant.status]}.
            </div>
          </div>
          {done.pricingError && <div className="banner warn">Müşteri açıldı ama özel fiyat kaydedilemedi: {done.pricingError}. Fiyatlandırma sekmesinden yeniden deneyin.</div>}
          {done.password && (
            <div className="banner info-soft">
              <Icon name="key" />
              <div>
                <b>{owner.fullName}</b> ({owner.email}) için geçici şifre: <span className="mono big-code">{done.password}</span>
                <div style={{ fontWeight: 500 }}>Bu şifre bir daha gösterilmez. Kullanıcı ilk girişte kendi şifresini belirleyecek.</div>
              </div>
            </div>
          )}
          <div className="row">
            <a className="btn primary" href={`#/yonetim/musteriler/${done.tenant.id}`}>
              Müşteri sayfasına git
            </a>
            <a className="btn" href="#/yonetim/musteriler">
              Müşteri listesi
            </a>
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <div className="row">
        <a className="btn ghost" href="#/yonetim/musteriler">
          <Icon name="back" /> Müşteriler
        </a>
        <div className="timeline">
          {STEPS.map((s, i) => (
            <span key={s} className={i < step ? 'done' : i === step ? 'now' : ''}>
              {i + 1}. {s}
            </span>
          ))}
        </div>
      </div>
      <section className="card">
        <div className="card-b stack">
          {error && <div className="banner bad">{error}</div>}
          {step === 0 && (
            <>
              <CompanyFields value={company} onChange={setCompany} codeLocked={false} />
              <label className="row" style={{ gap: 8 }}>
                <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
                Hemen aktif olsun (işaretlenmezse "Kuruluyor" durumunda açılır, kullanıcıları giriş yapamaz)
              </label>
            </>
          )}
          {step === 1 && (
            <>
              <label className="row" style={{ gap: 8 }}>
                <input type="checkbox" checked={withOwner} onChange={(e) => setWithOwner(e.target.checked)} />
                Firma yöneticisi hesabını şimdi aç
              </label>
              {withOwner && (
                <div className="form-grid">
                  <div className="field">
                    <label htmlFor="o-name">Ad soyad</label>
                    <input id="o-name" className="input" value={owner.fullName} onChange={(e) => setOwner({ ...owner, fullName: e.target.value })} />
                  </div>
                  <div className="field">
                    <label htmlFor="o-email">E-posta (giriş adı)</label>
                    <input id="o-email" className="input" type="email" value={owner.email} onChange={(e) => setOwner({ ...owner, email: e.target.value })} />
                  </div>
                  <div className="field">
                    <label>Geçici şifre</label>
                    <div className="row" style={{ gap: 6 }}>
                      <span className="mono big-code">{owner.password}</span>
                      <button className="btn sm" onClick={() => setOwner({ ...owner, password: temporaryPassword() })}>
                        <Icon name="refresh" size={16} />
                      </button>
                    </div>
                    <span className="hint">İlk girişte kendi şifresini belirlemesi istenir</span>
                  </div>
                </div>
              )}
              <p className="muted">Firma yöneticisi kendi ekibine kullanıcı ekleyebilir, ürünlerini ve pazaryeri hesaplarını tanımlayabilir.</p>
            </>
          )}
          {step === 2 && (
            <>
              <div className="choice-list">
                <label className={`choice${pricingMode === 'list' ? ' on' : ''}`}>
                  <input type="radio" name="pricing" checked={pricingMode === 'list'} onChange={() => void usePricing('list')} />
                  <span>
                    <b>Liste fiyatı</b>
                    <small>Herkese uygulanan kargo ve elleçleme baremleri ile sabit ücretler</small>
                  </span>
                </label>
                <label className={`choice${pricingMode === 'custom' ? ' on' : ''}`}>
                  <input type="radio" name="pricing" checked={pricingMode === 'custom'} onChange={() => void usePricing('custom')} />
                  <span>
                    <b>Müşteriye özel fiyat</b>
                    <small>Bir şablondan (liste fiyatı veya başka bir müşterinin fiyatları) kopyalanır, burada düzenlenir</small>
                  </span>
                </label>
              </div>
              {pricingMode === 'custom' && (
                <>
                  <TemplatePicker
                    tenants={tenants.data ?? []}
                    value={template}
                    onChange={(v) => {
                      setTemplate(v);
                      void usePricing('custom', v);
                    }}
                  />
                  {draft ? <PricingEditor draft={draft} onChange={setDraft} /> : <Spinner />}
                </>
              )}
            </>
          )}
          {step === 3 && (
            <dl className="summary">
              <dt>Müşteri</dt>
              <dd>
                <SellerChip code={company.code} /> {company.displayName} · {company.legalName}
              </dd>
              <dt>Vergi</dt>
              <dd>{company.taxNumber ? `${company.taxNumber}${company.taxOffice ? ` · ${company.taxOffice}` : ''}` : '—'}</dd>
              <dt>İletişim</dt>
              <dd>{[company.contactName, company.contactEmail, company.contactPhone].filter(Boolean).join(' · ') || '—'}</dd>
              <dt>Durum</dt>
              <dd>{active ? 'Aktif' : 'Kuruluyor'}</dd>
              <dt>Firma yöneticisi</dt>
              <dd>{withOwner ? `${owner.fullName} · ${owner.email}` : 'Sonra eklenecek'}</dd>
              <dt>Fiyat</dt>
              <dd>{pricingMode === 'list' ? 'Liste fiyatı' : `Özel fiyat, ${draft ? fmt.date(draft.validFrom) : ''} itibarıyla (${draft?.cards.length ?? 0} barem${draft?.tariff ? ' + sabit ücretler' : ''})`}</dd>
            </dl>
          )}
          {stepProblems.length > 0 && <div className="hint bad">{stepProblems.join(' · ')}</div>}
          <div className="row">
            {step > 0 && (
              <button className="btn" onClick={() => setStep(step - 1)}>
                Geri
              </button>
            )}
            {step < STEPS.length - 1 ? (
              <button className="btn primary" disabled={stepProblems.length > 0 || (step === 2 && pricingMode === 'custom' && !draft)} onClick={() => setStep(step + 1)}>
                Devam
              </button>
            ) : (
              <button className="btn primary" disabled={busy || problems.length > 0} onClick={() => void create()}>
                <Icon name="check" /> Müşteriyi aç
              </button>
            )}
          </div>
        </div>
      </section>
    </>
  );
}

/**
 * KVKK: how long this seller's closed orders keep their buyer's data. A warehouse admin gives the
 * warehouse's own companies (the data controller of their buyers' data) a longer period; every
 * other seller stays on the default. Supervisors see it; the seller's own view leaves it out.
 */
function RetentionCard({ tenant, onSaved }: { tenant: TenantDetail; onSaved: () => void }) {
  const session = useSession();
  const toast = useToast();
  const [form, setForm] = useState<{ own: boolean; days: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const retention = tenant.personalDataRetention;
  if (!retention) return null;

  const editing = form ?? { own: retention.days !== null, days: retention.days === null ? '' : String(retention.days) };
  const days = editing.own ? (editing.days === '' ? Number.NaN : Number(editing.days)) : null;
  const problem = sellerRetentionProblem(days);
  const dirty = form !== null && days !== retention.days;
  const amazonDays = Math.min(retention.defaultDays, PRIVACY_DEFAULTS.amazonMaxDays);
  const label = (d: number | null) => (d === null ? `varsayılan (${retention.defaultDays} gün)` : `${d} gün`);

  const save = async () => {
    if (problem) return setError(problem);
    // a shorter period erases at the next night's run what is older than it: irreversible
    if ((days ?? retention.defaultDays) < (retention.days ?? retention.defaultDays)) {
      const question =
        `${tenant.code} için saklama süresi ${label(retention.days)} yerine ${label(days)} olsun mu? ` +
        `Bundan eski kapanmış siparişlerin alıcı verisi bu gece silinir ve geri getirilemez.`;
      if (!window.confirm(question)) return;
    }
    setBusy(true);
    setError(null);
    try {
      await api('PATCH', `/api/admin/tenants/${tenant.id}`, { personalDataRetentionDays: days });
      setForm(null);
      toast(`Alıcı kişisel verisi saklama süresi: ${label(days)}`, 'ok');
      onSaved();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card">
      <div className="card-h">
        <Icon name="shield" />
        <h2>Alıcıların kişisel verisi (KVKK)</h2>
        <span className="sub">Ayrıntı: docs/kvkk.md</span>
      </div>
      <div className="card-b stack">
        {error && <div className="banner bad">{error}</div>}
        <fieldset disabled={!session.isAdmin || busy} className="plain">
          <div className="form-grid">
            <div className="field">
              <label htmlFor="t-retention">Alıcı kişisel verisi saklama süresi</label>
              <select
                id="t-retention"
                className="select"
                value={editing.own ? 'own' : 'default'}
                onChange={(e) => setForm({ ...editing, own: e.target.value === 'own' })}
              >
                <option value="default">Varsayılan ({retention.defaultDays} gün)</option>
                <option value="own">Kendi süresi</option>
              </select>
            </div>
            {editing.own && (
              <div className="field">
                <label htmlFor="t-retention-days">
                  Gün ({SELLER_RETENTION_DAYS.min}–{SELLER_RETENTION_DAYS.max})
                </label>
                <input
                  id="t-retention-days"
                  className={`input mono${editing.days !== '' && problem ? ' invalid' : ''}`}
                  inputMode="numeric"
                  maxLength={4}
                  placeholder="730"
                  value={editing.days}
                  onChange={(e) => setForm({ own: true, days: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                />
                {editing.days !== '' && problem && <span className="hint bad">{problem}</span>}
              </div>
            )}
          </div>
        </fieldset>
        <span className="hint">
          Sipariş kapandıktan (teslim, iptal, iade) bu kadar gün sonra alıcının adı, telefonu, e-postası, adres satırları ve etiket / e-belge içerikleri silinir
          (açık bir iade bunu en çok {PRIVACY_DEFAULTS.returnWaitDays} gün erteler); ilçe, il ve sipariş bilgileri kalır. Daha uzun süre yalnız deponun kendi
          şirketleri içindir: alıcı verisinin veri sorumlusu onlardır. Diğer satıcılar varsayılanda kalır. Amazon siparişlerinin verisi her durumda, açık iade
          olsa da, en geç {amazonDays} gün sonra silinir (Amazon'un veri koruma kuralı). Satıcı bu ayarı görmez; yalnız depo yöneticisi değiştirir.
        </span>
        {session.isAdmin && (
          <div className="row">
            <button className="btn primary" disabled={!dirty || busy || problem !== null} onClick={() => void save()}>
              Kaydet
            </button>
            {form && (
              <button className="btn ghost" onClick={() => setForm(null)}>
                Değişiklikleri geri al
              </button>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

type DetailTab = 'info' | 'users' | 'pricing';

/** One customer: details, users and prices; activation and suspension. */
export function TenantPage({ id }: { id: string }) {
  const session = useSession();
  const toast = useToast();
  const tenant = useLoad(() => api<TenantDetail>('GET', `/api/admin/tenants/${id}`), [id]);
  const tenants = useLoad(() => api<TenantSummary[]>('GET', '/api/admin/tenants'), []);
  const [tab, setTab] = useState<DetailTab>('info');
  const [form, setForm] = useState<CompanyForm | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (tenant.error) return <ErrorBanner message={tenant.error} onRetry={() => void tenant.reload()} />;
  if (!tenant.data) return <Spinner />;
  const t = tenant.data;
  const editing = form ?? companyFrom(t);
  const dirty = form !== null;

  const save = async () => {
    const problems = companyProblems(editing, false);
    if (problems.length > 0) return setError(problems.join(' · '));
    setBusy(true);
    setError(null);
    try {
      await api('PATCH', `/api/admin/tenants/${id}`, companyBody(editing));
      setForm(null);
      toast('Müşteri bilgileri kaydedildi', 'ok');
      void tenant.reload();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const setStatus = async (status: TenantStatus, question: string) => {
    if (!window.confirm(question)) return;
    try {
      await api('PATCH', `/api/admin/tenants/${id}`, { status });
      toast(`Durum: ${TENANT_STATUS_LABELS[status]}`, 'ok');
      void tenant.reload();
    } catch (e) {
      toast(errorMessage(e), 'bad');
    }
  };

  return (
    <>
      <div className="row">
        <a className="btn ghost" href="#/yonetim/musteriler">
          <Icon name="back" /> Müşteriler
        </a>
        <SellerChip code={t.code} />
        <h2 style={{ margin: 0 }}>{t.displayName}</h2>
        <span className={`chip ${STATUS_TONE[t.status]}`}>{TENANT_STATUS_LABELS[t.status]}</span>
        <span className="spacer" style={{ flex: 1 }} />
        {session.isAdmin && (
          <div className="row" style={{ gap: 6 }}>
            {t.status !== 'active' && t.status !== 'offboarded' && (
              <button className="btn primary sm" onClick={() => void setStatus('active', `${t.displayName} aktifleştirilsin mi? Kullanıcıları giriş yapabilecek.`)}>
                Aktifleştir
              </button>
            )}
            {t.status === 'active' && (
              <button className="btn sm" onClick={() => void setStatus('suspended', `${t.displayName} askıya alınsın mı? Kullanıcılarının oturumları hemen kapanır.`)}>
                Askıya al
              </button>
            )}
            {t.status === 'suspended' && (
              <button className="btn sm" onClick={() => void setStatus('offboarded', `${t.displayName} ayrıldı olarak işaretlensin mi?`)}>
                Ayrıldı
              </button>
            )}
          </div>
        )}
      </div>
      <div className="grid">
        <div className="kpi">
          <span className="n">{t.activeUsers}</span>
          <span className="l">Aktif kullanıcı</span>
        </div>
        <div className="kpi">
          <span className="n">{fmt.n(t.products)}</span>
          <span className="l">Ürün</span>
        </div>
        <div className="kpi">
          <span className="n">{fmt.n(t.unitsInStock)}</span>
          <span className="l">Stoktaki adet</span>
        </div>
        <div className="kpi">
          <span className="n">{fmt.n(t.openOrders)}</span>
          <span className="l">Açık sipariş</span>
        </div>
      </div>
      <div className="tabs" role="tablist">
        {(
          [
            ['info', 'Firma bilgileri'],
            ['users', 'Kullanıcılar'],
            ['pricing', 'Fiyatlandırma'],
          ] as const
        ).map(([key, label]) => (
          <button key={key} className={tab === key ? 'on' : ''} role="tab" aria-selected={tab === key} onClick={() => setTab(key)}>
            {label}
          </button>
        ))}
      </div>
      {tab === 'info' && (
        <section className="card">
          <div className="card-b stack">
            {error && <div className="banner bad">{error}</div>}
            <fieldset disabled={!session.isAdmin} className="plain">
              <CompanyFields value={editing} onChange={setForm} codeLocked />
            </fieldset>
            <div className="row">
              {session.isAdmin && (
                <button className="btn primary" disabled={!dirty || busy} onClick={() => void save()}>
                  Kaydet
                </button>
              )}
              {dirty && (
                <button className="btn ghost" onClick={() => setForm(null)}>
                  Değişiklikleri geri al
                </button>
              )}
              <span className="muted" style={{ fontSize: 13 }}>
                Açılış {fmt.date(t.createdAt)} · son değişiklik {fmt.dateTime(t.updatedAt)}
              </span>
            </div>
          </div>
        </section>
      )}
      {tab === 'info' && <RetentionCard tenant={t} onSaved={() => void tenant.reload()} />}
      {tab === 'users' && <UsersPanel kind="tenant" tenantId={t.id} tenants={tenants.data ?? []} />}
      {tab === 'pricing' && (
        <section className="card">
          <div className="card-b">
            <PricingPanel tenant={t} tenants={tenants.data ?? []} canEdit={session.isAdmin} />
          </div>
        </section>
      )}
    </>
  );
}
